import { SettingKey, WizardStepName } from 'data/ppoints/constants';
import useWizard from 'features/wizard/hooks/useWizard';
import useErrorBanner from 'hooks/error-banner/useErrorBanner';
import useOpenHelpCenter from 'hooks/help-center/useOpenHelpCenter';
import { useCarrierService } from 'hooks/ppoints/useCarrierService';
import { useSetAppMetafield } from 'hooks/script-tag/useSetAppMetafield';
import useSettings from 'hooks/useSettings';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import { toggleDeliveryCustomizationMutation } from 'mutations/shopify-delivery-customization/toggle-delivery-customization';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface SetIntegrationModeOptions {
    /**
     * Completes the wizard's integration step once the mode is saved (default `true`). Off for the wizard itself,
     * which completes the step on its own, and for the silent default-mode fallback.
     */
    completeWizardStep?: boolean;
}

/**
 * Switches the shop to an integration mode:
 * 1. the checkout list needs Shopify's carrier service, so it's created for `checkout` and deleted for every other mode
 * 2. the mode itself is saved
 * 3. the `settings.smart_delivery_page_integration_enabled` app metafield is set: `true` for the Smart Delivery Page,
 *    `false` for every other mode
 * 4. the delivery customization is switched on or off, as the caller asks (`deliveryCustomizationActive`)
 * 5. the wizard's integration step is completed, if the wizard isn't done yet (skipped with `completeWizardStep: false`)
 *
 * Resolves `false` and stops there when the carrier service request fails or the mode doesn't save. If creating the
 * carrier service fails, the global banner explains the Carrier-Calculated Shipping (CCS) requirement; if saving the
 * mode fails, it shows the generic activation error. Toasts are left to the caller.
 */
const useSetIntegrationMode = () => {
    const carrierService = useCarrierService();
    const integrationModeSetting = useSettings<IntegrationModeSetting>(SettingKey.integration_mode);
    const wizard = useWizard();
    const { setMetafield } = useSetAppMetafield();
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const { setBanner } = useErrorBanner();
    const openHelpCenter = useOpenHelpCenter(8286656, 'article', 'carrier_service_warning');

    // Same content as `CarrierServiceWarning`: the store needs Shopify's CCS for the checkout list
    const showCarrierServiceBanner = useCallback(() => {
        setBanner({
            titleKey: 'INTEGRATION_MODE.WARNING_BANNER.DESC',
            descriptionKey: 'INTEGRATION_MODE.WARNING_BANNER.PLANS',
            tone: 'warning',
            action: {
                content: t('INTEGRATION_MODE.WARNING_BANNER.MORE_DETAILS'),
                onAction: openHelpCenter,
            },
        });
    }, [setBanner, t, openHelpCenter]);

    const setIntegrationMode = useCallback(
        async (
            mode: IntegrationModeSetting,
            deliveryCustomizationActive: boolean,
            { completeWizardStep = true }: SetIntegrationModeOptions = {}
        ): Promise<boolean> => {
            setIsLoading(true);
            try {
                try {
                    if (mode === IntegrationModeSetting.checkout) {
                        await carrierService.createCarrierService();
                    } else {
                        await carrierService.deleteCarrierService();
                    }
                } catch {
                    showCarrierServiceBanner();
                    return false;
                }

                const saved = await integrationModeSetting.setSettings(mode);
                if (!saved) {
                    setBanner({
                        titleKey: 'WIZARD.INTEGRATION_MODE_STEP.ACTIVATE_ERROR.TITLE',
                        descriptionKey: 'WIZARD.INTEGRATION_MODE_STEP.ACTIVATE_ERROR.DESCRIPTION',
                        tone: 'warning',
                        withSupportActions: true,
                    });
                    return false;
                }

                // Tells the storefront whether the Smart Delivery Page is the active integration. A failure doesn't undo
                // the already saved mode, so it's only logged
                await setMetafield({
                    namespace: 'settings',
                    key: 'smart_delivery_page_integration_enabled',
                    type: 'boolean',
                    value: String(mode === IntegrationModeSetting.smart_delivery_page),
                }).catch((error) => console.error('Failed to set the Smart Delivery Page metafield', error));

                // Not awaited: a failure here only shows its banner, the mode is already switched
                toggleDeliveryCustomizationMutation(deliveryCustomizationActive).catch(() => {
                    setBanner({
                        descriptionKey: deliveryCustomizationActive
                            ? 'SETTINGS.ERROR_DELIVERY_CUSTOMIZATION.ACTIVATE'
                            : 'SETTINGS.ERROR_DELIVERY_CUSTOMIZATION.DEACTIVATE',
                        titleKey: 'SETTINGS.ERROR_DELIVERY_CUSTOMIZATION.TITLE',
                        tone: 'warning',
                        withSupportActions: true,
                    });
                });

                if (completeWizardStep && !wizard.isAllDone()) {
                    await wizard.handleWizardStepper(WizardStepName.integration_mode_step);
                }

                return true;
            } finally {
                setIsLoading(false);
            }
        },
        [carrierService, integrationModeSetting, wizard, showCarrierServiceBanner, setBanner, setMetafield]
    );

    return { setIntegrationMode, isLoading };
};

export default useSetIntegrationMode;
